import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Briefcase, FileText, Loader, ChevronRight, Search,
  Star, Clock, Edit3, Trash2,
  Zap, ChevronDown, Calendar, Upload,
} from 'lucide-react';
import { templateApi, type Template, type TemplateSection } from '../../services/templateApi';
import { keyFindingsApi } from '../../services/keyFindingsApi';
import { reportApi, type Report } from '../../services/reportApi';
import { expertProfileApi } from '../../services/expertProfileApi';
import { useNavigate } from 'react-router-dom';
import { useAskSidebar } from '../../contexts/AskSidebarContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDocumentProgress } from '../../contexts/DocumentProgressContext';
import { documentApi } from '../../services/api';
import { ReadingProgressIndicator } from './documents/ReadingProgressIndicator';
import { SectionManagerView, type SectionState } from './SectionManagerView';
import { ConversationalSetupView, type ReportPreferences } from './ConversationalSetupView';
import { GenerationProgressView, hasActiveRunner } from './GenerationProgressView';

// ─── Category colours (matches TemplateListPage) ───────────────────────────
const CATEGORY_COLOURS: Record<string, { bg: string; text: string; dot: string }> = {
  'Neurology':              { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
  'Orthopaedics':           { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  'Psychiatry':             { bg: 'bg-teal-50 dark:bg-teal-900/20', text: 'text-teal-700 dark:text-teal-300', dot: 'bg-teal-500' },
  'Pain Medicine':          { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  'General Surgery':        { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-500' },
  'Radiology':              { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  'Cardiology':             { bg: 'bg-rose-50 dark:bg-rose-900/20', text: 'text-rose-700 dark:text-rose-300', dot: 'bg-rose-500' },
  'Obstetrics & Gynaecology': { bg: 'bg-pink-50 dark:bg-pink-900/20', text: 'text-pink-700 dark:text-pink-300', dot: 'bg-pink-500' },
  'Respiratory Medicine':   { bg: 'bg-cyan-50 dark:bg-cyan-900/20', text: 'text-cyan-700 dark:text-cyan-300', dot: 'bg-cyan-500' },
  'ENT':                    { bg: 'bg-indigo-50 dark:bg-indigo-900/20', text: 'text-indigo-700 dark:text-indigo-300', dot: 'bg-indigo-500' },
  'Medical-Legal':          { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
  'Legal':                  { bg: 'bg-slate-50 dark:bg-slate-900/20', text: 'text-slate-700 dark:text-slate-300', dot: 'bg-slate-500' },
  'Clinical':               { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
  'Summary':                { bg: 'bg-gray-50 dark:bg-gray-900/20', text: 'text-gray-700 dark:text-gray-300', dot: 'bg-gray-500' },
};

const DEFAULT_COLOUR = { bg: 'bg-medical-50 dark:bg-medical-800', text: 'text-medical-700 dark:text-medical-300', dot: 'bg-medical-400' };

// ─── Persona tagging ────────────────────────────────────────────────────────
type PersonaType = 'EXPERT_WITNESS' | 'SOLICITOR';
const ALL_PERSONAS: PersonaType[] = ['EXPERT_WITNESS', 'SOLICITOR'];

/** Derive likely target personas for a DB template from its free-text category. */
function categoryToPersonas(category: string | null | undefined): PersonaType[] {
  switch (category) {
    case 'Solicitor':
    case 'Legal':
      return ['SOLICITOR'];
    // Medical-Legal templates default to expert authorship (e.g. expert reports).
    // Solicitor-facing templates within this category should be re-categorised
    // as 'Solicitor' or 'Legal' rather than relying on the default mapping.
    case 'Medical-Legal':
    case 'Clinical':
    case 'Neurology':
    case 'Orthopaedics':
    case 'Psychiatry':
    case 'Pain Medicine':
    case 'General Surgery':
    case 'Radiology':
    case 'Cardiology':
    case 'Obstetrics & Gynaecology':
    case 'Respiratory Medicine':
    case 'ENT':
      return ['EXPERT_WITNESS'];
    default:
      return ALL_PERSONAS;
  }
}

// ─── Built-in "Summary of Findings" quick-action template ───────────────────
const KEY_FINDINGS_SECTIONS: TemplateSection[] = [
  { id: 'intro', title: 'Introduction', placeholder: 'Brief introduction to the case, the claimant, and the scope of the records reviewed.', required: true, aiGenerated: true, order: 0 },
  { id: 'key-findings', title: 'Key Findings', placeholder: 'Synthesise the starred key findings from the case records, organised by clinical significance. Highlight diagnoses, treatments, outcomes, and any notable gaps or inconsistencies.', required: true, aiGenerated: true, order: 1 },
  { id: 'analysis', title: 'Analysis', placeholder: 'Analyse the key findings in context — their clinical significance, interrelationships, and implications for the case.', required: true, aiGenerated: true, order: 2 },
  { id: 'summary-obs', title: 'Summary & Observations', placeholder: 'A brief closing summary tying together the key findings. Note any areas where the records are incomplete or where further investigation may be warranted.', required: true, aiGenerated: true, order: 3 },
];

const KEY_FINDINGS_TEMPLATE: Template = {
  id: '__builtin_key_findings__',
  name: 'Key Findings Report',
  category: 'Summary',
  description: 'Report synthesising the starred key findings from the case records.',
  isPublic: true,
  createdBy: 'system',
  sections: JSON.stringify(KEY_FINDINGS_SECTIONS),
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// ─── Popular built-in templates ─────────────────────────────────────────────
function makeBuiltinTemplate(id: string, name: string, category: string, description: string, personas: PersonaType[], sections: Omit<TemplateSection, 'id' | 'required' | 'order'>[]): { template: Template; sections: TemplateSection[]; personas: PersonaType[] } {
  const parsed: TemplateSection[] = sections.map((s, i) => ({ ...s, id: `${id}-${i}`, required: true, order: i }));
  return {
    template: { id: `__builtin_${id}__`, name, category, description, isPublic: true, createdBy: 'system', sections: JSON.stringify(parsed), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
    sections: parsed,
    personas,
  };
}

const CHRONOLOGY_BUILTIN = makeBuiltinTemplate('chronology-summary', 'Chronology Summary', 'Summary', 'Structured chronological narrative of the medical records.', ALL_PERSONAS, [
  { title: 'Introduction', placeholder: 'Identify the claimant, the instructing party, and the records reviewed.', aiGenerated: true },
  { title: 'Chronological Summary', placeholder: 'A detailed chronological account of all significant clinical events, consultations, investigations, and treatments documented in the records.', aiGenerated: true },
  { title: 'Key Observations', placeholder: 'Highlight notable patterns, gaps in records, or inconsistencies observed across the chronology.', aiGenerated: true },
]);

const POPULAR_TEMPLATES = [
  CHRONOLOGY_BUILTIN,
  makeBuiltinTemplate('expert-witness', 'Expert Witness Report', 'Medical-Legal', 'Full expert witness report suitable for CPR Part 35 compliance.', ['EXPERT_WITNESS'], [
    { title: 'Introduction & Instructions', placeholder: 'State the expert\'s qualifications, instructions received, and documents reviewed.', aiGenerated: true },
    { title: 'Medical History', placeholder: 'Chronological summary of the relevant medical history drawn from the records.', aiGenerated: true },
    { title: 'Current Condition', placeholder: 'Description of the claimant\'s current clinical presentation and functional status.', aiGenerated: false },
    { title: 'Opinion', placeholder: 'The expert\'s opinion on causation, condition, and prognosis, supported by the evidence.', aiGenerated: false },
    { title: 'Summary of Conclusions', placeholder: 'Concise numbered list of the expert\'s conclusions.', aiGenerated: false },
    { title: 'Statement of Truth & Declaration', placeholder: 'CPR Part 35 statement of truth and declaration of duty to the court.', aiGenerated: true },
  ]),
  makeBuiltinTemplate('medical-negligence', 'Medical Negligence Report', 'Medical-Legal', 'Structured report for clinical negligence claims addressing breach and causation.', ['EXPERT_WITNESS'], [
    { title: 'Introduction & Instructions', placeholder: 'State the expert\'s qualifications, instructions, and documents reviewed.', aiGenerated: true },
    { title: 'Background & Medical History', placeholder: 'Comprehensive chronological account of the relevant medical history.', aiGenerated: true },
    { title: 'The Index Event', placeholder: 'Detailed account of the clinical episode under scrutiny.', aiGenerated: true },
    { title: 'Standard of Care', placeholder: 'Discussion of the expected standard of care applicable at the material time.', aiGenerated: false },
    { title: 'Breach of Duty', placeholder: 'Analysis of whether the care provided fell below the expected standard.', aiGenerated: false },
    { title: 'Causation & Prognosis', placeholder: 'Assessment of causation and the claimant\'s prognosis.', aiGenerated: false },
    { title: 'Summary of Conclusions', placeholder: 'Numbered conclusions.', aiGenerated: false },
  ]),
  makeBuiltinTemplate('clinical-incident', 'Clinical Incident Review', 'Clinical', 'Records-based review of a clinical incident for advisory or pre-action purposes.', ['EXPERT_WITNESS'], [
    { title: 'Introduction', placeholder: 'Brief outline of the incident and the purpose of this review.', aiGenerated: true },
    { title: 'Relevant Medical History', placeholder: 'Chronological summary of the medical history relevant to the incident.', aiGenerated: true },
    { title: 'The Incident', placeholder: 'Factual account of the clinical incident as documented in the records.', aiGenerated: true },
    { title: 'Analysis & Observations', placeholder: 'Observations on the care provided, referencing relevant guidelines or standards where applicable.', aiGenerated: false },
    { title: 'Recommendations', placeholder: 'Recommendations for next steps — further records requests, expert instruction, or areas of concern.', aiGenerated: false },
  ]),
];

function getCategoryColour(category: string) {
  return CATEGORY_COLOURS[category] || DEFAULT_COLOUR;
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Parse ProseMirror JSON content and extract H2 sections with their fill status. */
function deriveSectionsFromContent(content: string): SectionState[] {
  try {
    const doc = JSON.parse(content);
    if (!doc?.content || !Array.isArray(doc.content)) return [];

    const sections: SectionState[] = [];
    const nodes = doc.content as Array<{ type: string; attrs?: { level?: number }; content?: Array<{ text?: string }> }>;

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (node.type === 'heading' && node.attrs?.level === 2) {
        const title = node.content?.map(c => c.text || '').join('') || 'Untitled';
        let hasContent = false;
        for (let j = i + 1; j < nodes.length; j++) {
          if (nodes[j].type === 'heading' && (nodes[j].attrs?.level ?? 99) <= 2) break;
          const text = nodes[j].content?.map(c => c.text || '').join('').trim() || '';
          if (text.length > 0) { hasContent = true; break; }
        }
        sections.push({
          id: `section-${i}`,
          title,
          status: hasContent ? 'draft' : 'empty',
        });
      }
    }
    return sections;
  } catch {
    return [];
  }
}

/** Suggest a unique title given existing report titles. */
function suggestUniqueTitle(baseName: string, existingTitles: string[]): string {
  if (!existingTitles.includes(baseName)) return baseName;
  let n = 2;
  while (existingTitles.includes(`${baseName} (${n})`)) n++;
  return `${baseName} (${n})`;
}

// ─── Sub-views ──────────────────────────────────────────────────────────────
type WizardView = 'landing' | 'setup' | 'generating' | 'editor';

// ─── Simple Template Card (no expand, no inline form) ───────────────────────
const TemplateCard: React.FC<{
  template: Template;
  onSelect: () => void;
}> = ({ template, onSelect }) => {
  const sections = useMemo(() => templateApi.parseSections(template.sections), [template.sections]);
  const colour = getCategoryColour(template.category);

  return (
    <button
      onClick={onSelect}
      className="w-full text-left rounded-lg border border-medical-100 dark:border-medical-600 hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-sm p-2.5 transition-all group"
    >
      <p className="text-xs font-medium text-medical-800 dark:text-medical-200 group-hover:text-primary-700 dark:group-hover:text-primary-400 transition-colors mb-0.5">
        {template.name}
      </p>
      {template.description && (
        <p className="text-[11px] text-medical-500 dark:text-medical-400 leading-snug line-clamp-2 mb-1">
          {template.description}
        </p>
      )}
      <div className="flex items-center gap-2 text-[10px] text-medical-400 dark:text-medical-500">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${colour.bg} ${colour.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${colour.dot}`} />
          {template.category}
        </span>
        <span>{sections.length} sections</span>
      </div>
    </button>
  );
};

// ─── Inline Template Picker ─────────────────────────────────────────────────
const InlineTemplatePicker: React.FC<{
  onSelectTemplate: (template: Template, sections: TemplateSection[]) => void;
  popularTemplates?: Array<{ template: Template; sections: TemplateSection[]; personas: PersonaType[] }>;
}> = ({ onSelectTemplate, popularTemplates }) => {
  const { user } = useAuth();
  const myPersona: PersonaType = user?.personaType ?? 'EXPERT_WITNESS';

  const [searchTerm, setSearchTerm] = useState('');
  const [personaFilter, setPersonaFilter] = useState<'me' | 'all'>('me');

  // If the user's persona changes (e.g. switched in Account settings), realign default.
  useEffect(() => {
    setPersonaFilter('me');
  }, [user?.personaType]);

  const { data: templates, isLoading } = useQuery(
    ['templates'],
    () => templateApi.getTemplates(),
    { staleTime: 30000 }
  );

  const matchesPersona = useCallback((personas: PersonaType[]) => {
    return personaFilter === 'all' || personas.includes(myPersona);
  }, [personaFilter, myPersona]);

  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    const lower = searchTerm.toLowerCase();
    return templates.filter(t => {
      if (!matchesPersona(categoryToPersonas(t.category))) return false;
      if (!searchTerm) return true;
      return (
        t.name.toLowerCase().includes(lower) ||
        t.category.toLowerCase().includes(lower)
      );
    });
  }, [templates, searchTerm, matchesPersona]);

  // Also filter popular templates when searching
  const filteredPopular = useMemo(() => {
    if (!popularTemplates) return [];
    const lower = searchTerm.toLowerCase();
    return popularTemplates.filter(({ template: t, personas }) => {
      if (!matchesPersona(personas)) return false;
      if (!searchTerm) return true;
      return (
        t.name.toLowerCase().includes(lower) ||
        t.category.toLowerCase().includes(lower)
      );
    });
  }, [popularTemplates, searchTerm, matchesPersona]);

  const navigate = useNavigate();

  const templatesByCategory = useMemo(() => {
    return filteredTemplates.reduce((acc, template) => {
      const category = template.category || 'Uncategorised';
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    }, {} as Record<string, Template[]>);
  }, [filteredTemplates]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader className="h-4 w-4 animate-spin text-primary-600" />
      </div>
    );
  }

  const noResults = filteredTemplates.length === 0 && filteredPopular.length === 0;

  return (
    <div>
      {/* Search */}
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-medical-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search templates..."
          className="w-full pl-8 pr-3 py-1.5 text-xs border border-medical-200 dark:border-medical-600 rounded-md bg-white dark:bg-medical-700 text-medical-800 dark:text-medical-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {/* Persona filter */}
      <div className="inline-flex items-center gap-0.5 p-0.5 mb-3 rounded-md border border-medical-200 dark:border-medical-600 bg-medical-50 dark:bg-medical-800">
        <button
          type="button"
          onClick={() => setPersonaFilter('me')}
          className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
            personaFilter === 'me'
              ? 'bg-white dark:bg-medical-700 text-primary-700 dark:text-primary-300 shadow-sm'
              : 'text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200'
          }`}
        >
          For me
        </button>
        <button
          type="button"
          onClick={() => setPersonaFilter('all')}
          className={`px-2.5 py-1 text-[11px] font-medium rounded transition-colors ${
            personaFilter === 'all'
              ? 'bg-white dark:bg-medical-700 text-primary-700 dark:text-primary-300 shadow-sm'
              : 'text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200'
          }`}
        >
          All
        </button>
      </div>

      <div className="space-y-3">
        {/* Popular section */}
        {filteredPopular.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider px-1 mb-1.5">
              Popular
            </p>
            <div className="space-y-1.5">
              {filteredPopular.map(({ template, sections }) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={() => onSelectTemplate(template, sections)}
                />
              ))}
            </div>
          </div>
        )}

        {/* DB templates by category */}
        {Object.entries(templatesByCategory)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([category, categoryTemplates]) => (
            <div key={category}>
              <p className="text-[10px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wider px-1 mb-1.5">
                {category}
              </p>
              <div className="space-y-1.5">
                {categoryTemplates.map(t => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    onSelect={() => {
                      const parsedSections = templateApi.parseSections(t.sections);
                      onSelectTemplate(t, parsedSections);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}

        {noResults && (
          <div className="p-6 text-center">
            <FileText className="h-8 w-8 text-medical-200 dark:text-medical-600 mx-auto mb-2" />
            <p className="text-xs text-medical-400">No templates found</p>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Delete confirmation for final reports ──────────────────────────────────
const DeleteConfirmation: React.FC<{
  report: Report;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ report, onConfirm, onCancel }) => {
  const [confirmText, setConfirmText] = useState('');
  const isComplete = report.status === 'complete';
  const canDelete = isComplete ? confirmText === report.title : true;

  return (
    <div className="px-3 py-2 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/40 rounded-lg">
      <p className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">
        Delete "{report.title}"?
      </p>
      {isComplete ? (
        <>
          <p className="text-[10px] text-red-600 dark:text-red-400 mb-1.5">
            This is a finalised report. Type the report name to confirm deletion.
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={report.title}
            className="w-full px-2 py-1 text-xs border border-red-200 dark:border-red-700 rounded bg-white dark:bg-medical-800 text-medical-800 dark:text-medical-200 focus:outline-none focus:ring-1 focus:ring-red-500 mb-1.5"
            autoFocus
          />
        </>
      ) : (
        <p className="text-[10px] text-red-600 dark:text-red-400 mb-1.5">
          This action cannot be undone.
        </p>
      )}
      <div className="flex gap-1.5">
        <button
          onClick={onConfirm}
          disabled={!canDelete}
          className="flex-1 px-2 py-1 text-[10px] font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed rounded transition-colors"
        >
          Delete
        </button>
        <button
          onClick={onCancel}
          className="flex-1 px-2 py-1 text-[10px] font-medium text-medical-600 dark:text-medical-400 bg-white dark:bg-medical-700 border border-medical-200 dark:border-medical-600 hover:bg-medical-50 dark:hover:bg-medical-600 rounded transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Module-level state — survives sidebar close / tab switch ────────────────
// Keyed by caseId so different cases don't collide.
interface PersistedWizardState {
  caseId: string;
  view: WizardView;
  activeReport: Report | null;
  activeSections: SectionState[];
  selectedTemplate: Template | null;
  selectedTemplateSections: TemplateSection[];
  preferences: ReportPreferences | null;
  generationTitle: string;
  focusHint?: string;
}
let _persistedState: PersistedWizardState | null = null;

function getPersistedState(caseId: string | undefined): PersistedWizardState | null {
  if (_persistedState && _persistedState.caseId === caseId) return _persistedState;
  return null;
}

// ─── Main WizardTab ─────────────────────────────────────────────────────────
export const WizardTab: React.FC = () => {
  const { pageContext, pendingReportId, clearPendingReportId, setActiveSidebarTab } = useAskSidebar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const caseId = pageContext.caseId;
  const queryClient = useQueryClient();

  // Restore persisted state if available for this case.
  // Capture at mount time only (via useState initialiser) so it doesn't change on re-renders.
  const [mountedWithPersisted] = useState(() => getPersistedState(caseId));

  const [view, setView] = useState<WizardView>(mountedWithPersisted?.view ?? 'landing');
  const [activeReport, setActiveReport] = useState<Report | null>(mountedWithPersisted?.activeReport ?? null);
  const [activeSections, setActiveSections] = useState<SectionState[]>(mountedWithPersisted?.activeSections ?? []);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(mountedWithPersisted?.selectedTemplate ?? null);
  const [selectedTemplateSections, setSelectedTemplateSections] = useState<TemplateSection[]>(mountedWithPersisted?.selectedTemplateSections ?? []);
  const [preferences, setPreferences] = useState<ReportPreferences | null>(mountedWithPersisted?.preferences ?? null);
  const [generationTitle, setGenerationTitle] = useState(mountedWithPersisted?.generationTitle ?? '');
  const [focusHint, setFocusHint] = useState<string | undefined>(mountedWithPersisted?.focusHint);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [reviewBannerDismissed, setReviewBannerDismissed] = useState(false);

  // Persist state on every change so it survives unmount
  useEffect(() => {
    if (!caseId) return;
    _persistedState = {
      caseId,
      view,
      activeReport,
      activeSections,
      selectedTemplate,
      selectedTemplateSections,
      preferences,
      generationTitle,
      focusHint,
    };
  }, [caseId, view, activeReport, activeSections, selectedTemplate, selectedTemplateSections, preferences, generationTitle, focusHint]);

  // Recovery: if we MOUNTED in 'generating' state (i.e. sidebar was closed mid-generation),
  // the SSE streams are gone. Find the partially-generated report and open it in the editor.
  // This only runs when the component mounts with persisted generating state — NOT when
  // transitioning to 'generating' during the current session.
  // Skip recovery if the generation runner is still actively streaming (e.g. sidebar was
  // maximised, causing a remount but not a loss of the SSE stream).
  const recoveryAttempted = useRef(false);
  useEffect(() => {
    if (mountedWithPersisted?.view !== 'generating') return;
    if (recoveryAttempted.current || !caseId) return;
    if (hasActiveRunner(caseId, mountedWithPersisted.generationTitle)) return;
    recoveryAttempted.current = true;

    const recover = async () => {
      try {
        const reports = await reportApi.getReportsByCase(caseId);
        const title = mountedWithPersisted.generationTitle;
        const match = reports.find(r => r.title === title);
        if (match) {
          const report = await reportApi.getReport(match.id);
          const sections = deriveSectionsFromContent(report.content);
          setActiveReport(report);
          setActiveSections(sections);
          setView('editor');
          queryClient.invalidateQueries(['caseReports', caseId]);
        } else {
          setView('landing');
        }
      } catch {
        setView('landing');
      }
    };
    recover();
  }, [mountedWithPersisted, caseId, queryClient]);

  // Fetch existing reports for this case
  const { data: existingReports } = useQuery(
    ['caseReports', caseId],
    () => reportApi.getReportsByCase(caseId!),
    { enabled: !!caseId, staleTime: 30_000 },
  );

  // Check if user has a professional profile
  const { data: expertProfile } = useQuery(
    ['expertProfile'],
    () => expertProfileApi.get(),
    { staleTime: 5 * 60_000, retry: false },
  );
  const hasExpertProfile = !!(expertProfile?.credentials && Object.keys(expertProfile.credentials).length > 0);

  // Reading progress for the case
  const { viewSummary } = useDocumentProgress();
  const { data: caseDocuments } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId!),
    { enabled: !!caseId, staleTime: 60_000 },
  );
  const caseTotalPages = useMemo(
    () => (caseDocuments || []).reduce((sum: number, d: any) => sum + (d.pageCount || 0), 0),
    [caseDocuments],
  );
  const caseViewedPages = viewSummary?.totalViewedPages ?? 0;
  const reviewPercentage = caseTotalPages > 0 ? Math.round((Math.min(caseViewedPages, caseTotalPages) / caseTotalPages) * 100) : 0;

  const existingTitles = useMemo(
    () => existingReports?.map(r => r.title) ?? [],
    [existingReports],
  );

  // Open a specific report when navigated from Reports page / Dashboard
  const pendingConsumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingReportId || !caseId) return;
    if (pendingConsumedRef.current === pendingReportId) return;
    pendingConsumedRef.current = pendingReportId;

    // Check if it's already in the fetched list first
    const cached = existingReports?.find(r => r.id === pendingReportId);
    if (cached) {
      const sections = deriveSectionsFromContent(cached.content);
      setActiveReport(cached);
      setActiveSections(sections);
      setView('editor');
      clearPendingReportId();
      return;
    }

    // Otherwise fetch it
    reportApi.getReport(pendingReportId).then(report => {
      const sections = deriveSectionsFromContent(report.content);
      setActiveReport(report);
      setActiveSections(sections);
      setView('editor');
    }).catch(err => {
      console.error('Failed to open report from pending ID:', err);
    }).finally(() => {
      clearPendingReportId();
    });
  }, [pendingReportId, caseId, existingReports, clearPendingReportId]);

  const handleBackToLanding = useCallback(() => {
    setView('landing');
    _persistedState = null;
  }, []);

  // Template selected → go to conversational setup
  const handleSelectTemplate = useCallback((template: Template, sections: TemplateSection[], hint?: string) => {
    setSelectedTemplate(template);
    setSelectedTemplateSections(sections);
    setFocusHint(hint);
    setView('setup');
  }, []);

  // Preferences ready → transition immediately (report creation happens in GenerationProgressView)
  const handlePreferencesReady = useCallback((prefs: ReportPreferences, title: string) => {
    if (!caseId || !user?.id || !selectedTemplate) return;
    setPreferences(prefs);
    setGenerationTitle(suggestUniqueTitle(title || selectedTemplate.name, existingTitles));
    setView('generating');
  }, [caseId, user?.id, selectedTemplate, existingTitles]);

  // Generation complete → go to editor
  const handleGenerationComplete = useCallback((report: Report, sections: SectionState[]) => {
    setActiveReport(report);
    setActiveSections(sections);
    setView('editor');
  }, []);

  // Open an existing report
  const handleOpenReport = useCallback((report: Report) => {
    const sections = deriveSectionsFromContent(report.content);
    setActiveReport(report);
    setActiveSections(sections);
    setView('editor');
  }, []);

  const handleDeleteReport = useCallback(async (reportId: string) => {
    try {
      await reportApi.deleteReport(reportId);
      queryClient.invalidateQueries(['caseReports', caseId]);
      setDeletingReportId(null);
    } catch (err) {
      console.error('Failed to delete report:', err);
    }
  }, [caseId, queryClient]);

  // Key findings count for Summary quick-action pill
  const { data: keyFindings } = useQuery(
    ['keyFindings', caseId],
    () => keyFindingsApi.getForCase(caseId!),
    { enabled: !!caseId, staleTime: 30_000 }
  );
  const totalFindings = keyFindings?.length ?? 0;

  // Collapsible sections
  const [reportsExpanded, setReportsExpanded] = useState(true);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);

  // ─── View routing ─────────────────────────────────────────────────────

  if (view === 'editor' && activeReport && caseId) {
    return (
      <SectionManagerView
        report={activeReport}
        sections={activeSections}
        caseId={caseId}
        onBack={handleBackToLanding}
        preferences={preferences ?? undefined}
      />
    );
  }

  if (view === 'generating' && selectedTemplate && caseId && preferences && user?.id) {
    return (
      <GenerationProgressView
        caseId={caseId}
        templateId={selectedTemplate.id}
        templateName={selectedTemplate.name}
        templateSections={selectedTemplateSections}
        userId={user.id}
        reportTitle={generationTitle}
        preferences={preferences}
        onComplete={(report, sections) => {
          queryClient.invalidateQueries(['caseReports', caseId]);
          handleGenerationComplete(report, sections);
        }}
        onError={(err) => console.error('Generation error:', err)}
      />
    );
  }

  if (view === 'setup' && selectedTemplate && caseId) {
    return (
      <ConversationalSetupView
        caseId={caseId}
        template={selectedTemplate}
        sections={selectedTemplateSections}
        onPreferencesReady={handlePreferencesReady}
        onBack={handleBackToLanding}
        focusHint={focusHint}
      />
    );
  }

  // ─── Landing view ─────────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto">
        {/* Greeting */}
        <div className="px-4 pt-4 pb-3">
          <h2 className="text-base font-semibold text-medical-900 dark:text-medical-100">
            {getGreeting()}
          </h2>
          <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">
            {existingReports && existingReports.length > 0
              ? 'Continue a draft or start a new report.'
              : 'Select a template to get started.'}
          </p>
        </div>

        {/* ── Professional profile suggestion ────────────────────────── */}
        {!hasExpertProfile && (
          <div className="mx-3 mb-3">
            <button
              onClick={() => navigate('/account?tab=credentials')}
              className="w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-600 hover:shadow-sm transition-all text-left"
            >
              <Briefcase className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                  Set up your professional profile
                </p>
                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5 leading-snug">
                  Your credentials will be used to populate generated reports.
                </p>
              </div>
            </button>
          </div>
        )}

        {/* ── Your reports (expanded by default) ───────────────────────── */}
        {existingReports && existingReports.length > 0 && (
          <div className="mx-3 mb-3">
            <button
              onClick={() => setReportsExpanded(!reportsExpanded)}
              className="flex items-center justify-between w-full px-1 mb-2 group/header"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-purple-500 dark:text-purple-400" />
                <p className="text-xs font-semibold text-medical-700 dark:text-medical-300">
                  Your reports
                </p>
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  {existingReports.length}
                </span>
              </div>
              <ChevronDown className={`h-3.5 w-3.5 text-medical-400 dark:text-medical-500 transition-transform ${reportsExpanded ? '' : '-rotate-90'}`} />
            </button>
            {reportsExpanded && (
              <div className="space-y-1.5">
                {existingReports.map((report) => {
                  if (deletingReportId === report.id) {
                    return (
                      <DeleteConfirmation
                        key={report.id}
                        report={report}
                        onConfirm={() => handleDeleteReport(report.id)}
                        onCancel={() => setDeletingReportId(null)}
                      />
                    );
                  }
                  const sections = deriveSectionsFromContent(report.content);
                  const sectionCount = sections.length;
                  const filledCount = sections.filter(s => s.status !== 'empty').length;
                  const updatedAt = new Date(report.updatedAt);
                  return (
                    <div
                      key={report.id}
                      className="flex items-center gap-1 group/row"
                    >
                      <button
                        onClick={() => handleOpenReport(report)}
                        className="flex-1 flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-medical-100 dark:border-medical-700 bg-white dark:bg-medical-800 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm transition-all group text-left min-w-0"
                      >
                        <div className="p-1.5 rounded-md bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 flex-shrink-0">
                          <Edit3 className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-medical-800 dark:text-medical-200 truncate">
                            {report.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {sectionCount > 0 && (
                              <span className="text-[10px] text-medical-400 dark:text-medical-500">
                                {filledCount}/{sectionCount} sections
                              </span>
                            )}
                            <span className="text-[10px] text-medical-400 dark:text-medical-500 flex items-center gap-0.5">
                              <Clock className="h-2.5 w-2.5" />
                              {updatedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}{' '}
                              {updatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="h-3.5 w-3.5 text-medical-300 dark:text-medical-600 group-hover:text-medical-500 transition-colors flex-shrink-0" />
                      </button>
                      <button
                        onClick={() => setDeletingReportId(report.id)}
                        className="p-1.5 rounded-md text-medical-300 dark:text-medical-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover/row:opacity-100 transition-all flex-shrink-0"
                        title="Delete report"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Start a new draft ────────────────────────────────────────── */}
        <div className="mx-3 mb-3 pt-3 border-t border-medical-100 dark:border-medical-700">
          <div className="flex items-center gap-2 px-1 mb-2">
            <Zap className="h-3.5 w-3.5 text-primary-500 dark:text-primary-400" />
            <p className="text-xs font-semibold text-medical-700 dark:text-medical-300">
              Start a new draft
            </p>
          </div>

          <div className="space-y-2">
            {/* Key Findings Report */}
            <button
              onClick={() => handleSelectTemplate(KEY_FINDINGS_TEMPLATE, KEY_FINDINGS_SECTIONS, 'key-findings')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 border border-amber-200 dark:border-amber-800/40 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all group text-left"
            >
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 group-hover:bg-amber-200 dark:group-hover:bg-amber-900/40 transition-colors flex-shrink-0">
                <Star className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                    Key Findings Report
                  </p>
                  {totalFindings > 0 && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-200/60 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                      {totalFindings} finding{totalFindings !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-amber-600/80 dark:text-amber-400/70 leading-snug mt-0.5">
                  Synthesise the starred findings into a structured report
                </p>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-amber-400 dark:text-amber-600 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors flex-shrink-0" />
            </button>

            {/* Chronology Summary */}
            <button
              onClick={() => handleSelectTemplate(CHRONOLOGY_BUILTIN.template, CHRONOLOGY_BUILTIN.sections, 'chronology')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/10 border border-primary-200 dark:border-primary-800/40 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all group text-left"
            >
              <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/40 transition-colors flex-shrink-0">
                <Calendar className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-primary-800 dark:text-primary-200">
                  Chronology Summary
                </p>
                <p className="text-[10px] text-primary-600/80 dark:text-primary-400/70 leading-snug mt-0.5">
                  Structured chronological narrative of the medical records
                </p>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-primary-400 dark:text-primary-600 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors flex-shrink-0" />
            </button>

            {/* Mirror an existing report to create a template */}
            <button
              onClick={() => navigate('/templates/new?upload=true')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-primary-300 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-primary-50/50 dark:hover:bg-primary-900/20 transition-all group text-left"
            >
              <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-500 dark:text-primary-400 group-hover:bg-primary-200 dark:group-hover:bg-primary-800 transition-colors flex-shrink-0">
                <Upload className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-primary-700 dark:text-primary-300">
                  Mirror a previous report
                </p>
                <p className="text-[10px] text-primary-500/80 dark:text-primary-400/70 leading-snug mt-0.5">
                  Upload a Word report to create a reusable template
                </p>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-primary-400 dark:text-primary-600 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors flex-shrink-0" />
            </button>

            {/* Use a template — expands to full browser */}
            <button
              onClick={() => setTemplatesExpanded(!templatesExpanded)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-medical-300 dark:border-medical-600 hover:border-medical-400 dark:hover:border-medical-500 hover:bg-medical-50/50 dark:hover:bg-medical-800/30 transition-all group text-left"
            >
              <div className="p-2 rounded-lg bg-medical-100 dark:bg-medical-700 text-medical-500 dark:text-medical-400 group-hover:bg-medical-200 dark:group-hover:bg-medical-600 transition-colors flex-shrink-0">
                <FileText className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-medical-700 dark:text-medical-300">
                  Use a template...
                </p>
                <p className="text-[10px] text-medical-400 dark:text-medical-500 leading-snug mt-0.5">
                  Browse specialism-specific report templates
                </p>
              </div>
              <ChevronDown className={`h-3.5 w-3.5 text-medical-400 dark:text-medical-500 transition-transform ${templatesExpanded ? '' : '-rotate-90'}`} />
            </button>
          </div>
        </div>

        {/* ── Expanded template browser ────────────────────────────────── */}
        {templatesExpanded && (
          <div className="mx-3 pb-4">
            {/* Search */}
            <InlineTemplatePicker
              onSelectTemplate={handleSelectTemplate}
              popularTemplates={POPULAR_TEMPLATES}
            />
          </div>
        )}
      </div>

      {/* ── Review progress footer banner ────────────────────────────── */}
      {caseTotalPages > 0 && reviewPercentage < 100 && !reviewBannerDismissed && (
        <div className="shrink-0 border-t border-yellow-200 dark:border-yellow-900/50 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/15 dark:to-amber-900/15 px-3 py-2.5">
          <div className="flex items-start gap-2.5">
            <ReadingProgressIndicator
              viewedPages={caseViewedPages}
              totalPages={caseTotalPages}
              className="shrink-0 mt-0.5"
              hideLabel
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                {caseViewedPages} of {caseTotalPages} pages viewed ({reviewPercentage}%)
              </p>
              <p className="text-xs text-yellow-700/90 dark:text-yellow-300/90 mt-1 leading-snug">
                ClinLexis is an aid to your review. Always apply your own professional judgement.
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1.5 mt-0.5">
              <button
                onClick={() => setActiveSidebarTab('documents')}
                className="text-xs font-semibold text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/40 px-2.5 py-1 rounded transition-colors whitespace-nowrap"
              >
                Open records
              </button>
              <button
                onClick={() => setReviewBannerDismissed(true)}
                className="text-[11px] font-medium text-yellow-500 dark:text-yellow-500 hover:text-yellow-700 dark:hover:text-yellow-300 underline underline-offset-2 whitespace-nowrap"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
